import React from 'react'

import {Modal, ModalHeader, ModalBody, ModalFooter} from 'Components/Modal';
import {PrimaryButton, DangerButton, SecondaryButton} from 'Components/Button';
import {Loading, Alert} from 'Components/Partials';
import {Select, FileUpload} from 'Components/Form';

import AdminImportsApi from 'Services/Api/Admin/Imports';
import AdminCmsElementApi from 'Services/Api/Admin/Cms/Elements/Element';

import {translationFromJson} from 'Services/TranslationHelpers2';

export default class AdminRunImportModal extends React.Component  {
    /**
     * @var state
     */
    state = {
        alert: null,
        working: false,
        file: false,
        showTemplate: false,
        params: null,
        cms_element_options: null,
        availableColumns: this.props.selectedImport?.available_columns || null,
    };

    /**
     * @method componentDidUpdate
     */
    async componentDidMount() {
        if (this.props.selectedImport.import === 'CmsElementRecords') {
            await this.getCmsElements();
        }
    }

    /**
     * @method getCmsElements
     */
    getCmsElements = async () => {
        this.setState({working: true});

        const response = await AdminCmsElementApi.get(null, {
            import: true,
        });

        this.setState({
            working: false,
            cms_element_options: response.data.data.map((element) => (
                {
                    label: translationFromJson(element.label),
                    value: element.identifier,
                    available_columns: element.available_import_columns,
                }
            )),
        });
    }

    /**
     * @method handleParams
     * @param {string} key
     * @param {string} value
     */
    handleParams = (key, value) => {
        const {params} = this.state;

        this.setState({
            params: {
                ...params,
                [key]: value
            }
        });
    };

    /**
     * @method handleSubmit
     */
    handleSubmit = async () => {
        this.setState({
            alert: null,
            working: true
        });

        const response = await AdminImportsApi.post(null, {
            type: this.props.selectedImport.import,
            import_file: this.state.file,
            import_file_name: this.state.file.name,
            params: this.state.params
        });

        if (response.status !== 201) {
            this.setState({
                alert: {
                    type: 'error',
                    message: response.message,
                    errors: response.errors
                },
                working: false
            });
            return;
        }

        this.props.callbackOnSubmit(1);

        this.props.onClose();
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {alert, working, file, showTemplate, availableColumns } = this.state;
        const {selectedImport} = this.props;

        return (
            <Modal>
                <ModalBody>
                    <div className="sm:mx-auto sm:w-full">
                        <h2 className="my-2 text-center black text-2xl leading-9 font-extrabold">
                            {/* https://stackoverflow.com/questions/5582228/insert-space-before-capital-letters */}
                            { "Run " + selectedImport.import.replace(/([A-Z])/g, ' $1').trim() + " Import" }
                        </h2>

                        {alert !== null && (<Alert {...alert} />)}

                        {availableColumns &&
                            <div className="text-center my-4">
                                <PrimaryButton
                                    text={(showTemplate ? "Hide" : "Show") + " Template"}
                                    onClick={() => this.setState({showTemplate: !showTemplate})}
                                />

                                {showTemplate &&
                                    <table className="table-fixed mx-auto mt-2">
                                        <thead>
                                            <tr>
                                                <th className="px-4 py-2">Columns</th>
                                                <th className="px-4 py-2">Rules</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(availableColumns).map((column, i) => {
                                                return (
                                                    <tr key={i}>
                                                        <td className="px-4 py-2 border break-all">{column[0]}</td>
                                                        <td className="px-4 py-2 border">{column[1]}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }

                        <FileUpload
                            containerClassName="mt-4 flex flex-col justify-center items-center gap-4"
                            value={file}
                            onChange={v => this.setState({'file': v})}
                            accept=".csv"
                            placeholder="Select file"
                        />
                    </div>

                    {this.renderParams()}
                </ModalBody>

                <ModalFooter >
                    {working && (<Loading color="text-black"/>)}

                    {!working &&
                        <div className="w-full flex justify-between">
                            <div>
                                {availableColumns && (
                                    <PrimaryButton
                                        onClick={() => this.handleDownloadTemplate()}
                                        text="Download Template"
                                        type="button"
                                    />
                                )}
                            </div>

                            <div class="flex space-x-4">
                                <PrimaryButton text="Cancel" onClick={this.props.onClose} />
                                <DangerButton onClick={this.handleSubmit} text="Run" />
                            </div>
                        </div>
                    }
                </ModalFooter>
            </Modal>
        )
    }

    handleDownloadTemplate = () => {
        const headers = Object.keys(this.state.availableColumns);

        // Generate CSV content with only headers
        const csvContent = headers.join(',') + '\n' + Object.values(this.state.availableColumns).map((helpText) => {
            if (helpText.indexOf(',') !== -1) {
                // If the help text contains a comma, wrap it in quotes
                return `"${helpText}"`;
            }

            return helpText;
        }).join(',') + '\n';

        // Create a Blob from the CSV string
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a temporary anchor element
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'template.csv');

        // Append to body, trigger download, and remove link
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    handleCmsElement = (value) => {
        this.handleParams('cms_element_identifier', value);

        const cmsOption = this.state.cms_element_options.find(option => option.value === value);

        this.setState({
            availableColumns: cmsOption ? cmsOption.available_columns : null,
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    renderParams() {
        const {params, cms_element_options} = this.state;

        if (this.props.selectedImport.import === 'CmsElementRecords') {
            return (
                <div>
                    <Select
                        label="Cms Element"
                        value={params?.cms_element_identifier}
                        onChange={this.handleCmsElement}
                        options={cms_element_options}
                    />
                </div>
            );
        }

        return null;
    }
}
